import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import settingsJournalService from '../../services/settingsJournalService';
import Modal from '../common/Modal';
import Alert from '../common/Alert';
import SwitchButton from '../common/SwitchButton';
import { useAuth } from '../../hooks/useAuth';
import { getAppStorage } from '../../utils/authStorage';
import { hasPremiumAccess } from '../../utils/subscription';
import { APP_PREFIX } from '../../config/appIdentifiers';
import {
  AVAILABLE_MACROS,
  PREMIUM_ONLY_MACROS,
} from '../../config/nutritionMacros';
import {
  calculateMetabolicStats,
  calculateMacroTargets,
  calculateAdvancedMacroTargets,
  caloriesFromMacros,
  scaleMacrosToCalories,
} from '../../utils/nutritionCalculations';

const SUMMARY_STANDARD_MACRO_KEYS = Object.freeze(['proteins', 'carbohydrates', 'fat']);
const ADVANCED_SUMMARY_MACRO_KEYS = Object.freeze(['fiber', 'sugars', 'sodium', 'saturated-fat']);
const SUMMARY_ALLOWED_MACRO_KEYS = Object.freeze([
  ...SUMMARY_STANDARD_MACRO_KEYS,
  ...ADVANCED_SUMMARY_MACRO_KEYS
]);

const SITE_NAME = process.env.REACT_APP_SITE_NAME;
const SETTINGS_STORAGE_KEY = `${APP_PREFIX}-settings`;

const SettingsContainer = styled.div`
   max-width: 1200px;
   margin: 0 auto;
   padding: 0.5rem;
   font-family: 'Roboto', sans-serif;
   background-color: #F8F9FA;
   color: #333333;
   line-height: 1.5;
 
   @media (max-width: 480px) {
     padding: 0.25rem;
   }
 
   @media (min-width: 481px) and (max-width: 1024px) {
     padding: 0.75rem;
   }
 
   @media (min-width: 1025px) {
     padding: 1rem;
   }
 
   @media (min-width: 1024px) {
     padding: 1.5rem;
   }
 `;

const SettingsSection = styled.section`
   background: white;
   border-radius: 12px;
   padding: 0;
   margin-bottom: 1rem;
   box-shadow: 0 4px 12px rgba(26, 46, 64, 0.08);
   overflow: hidden;
 
   @media (max-width: 480px) {
     margin-bottom: 1rem;
   }
 
   @media (min-width: 481px) and (max-width: 1024px) {
     padding: 0;
   }
 
   @media (min-width: 1025px) {
     padding: 0;
   }
 `;

const SectionHeader = styled.div`
   background: linear-gradient(135deg, #2c5282 0%, #1A2E40 50%, #1a365d 100%);
   padding: 1rem;
   color: white;
   position: relative;
   overflow: hidden;
 
   &::before {
     content: '';
     position: absolute;
     top: 0;
     left: 0;
     right: 0;
     bottom: 0;
     background: linear-gradient(45deg, rgba(255, 255, 255, 0.1) 25%, transparent 25%),
       linear-gradient(-45deg, rgba(255, 255, 255, 0.1) 25%, transparent 25%),
       linear-gradient(45deg, transparent 75%, rgba(255, 255, 255, 0.1) 75%),
       linear-gradient(-45deg, transparent 75%, rgba(255, 255, 255, 0.1) 75%);
     background-size: 12px 12px;
     background-position: 0 0, 0 6px, 6px -6px, -6px 0px;
     opacity: 0.2;
     pointer-events: none;
   }
 `;

const SectionTitle = styled.h2`
   font-family: 'Poppins', 'Roboto', sans-serif;
   color: white;
   margin: 0;
   font-size: 1.1rem;
   font-weight: 700;
   display: flex;
   align-items: center;
   gap: 0.5rem;
   line-height: 1.3;
   letter-spacing: -0.02em;
   position: relative;
   z-index: 1;
 
   svg {
     fill: white;
     flex-shrink: 0;
   }
 
   @media (max-width: 480px) {
     font-size: 1rem;
     flex-wrap: wrap;
   }
 
   @media (min-width: 1025px) {
     font-size: 1.15rem;
   }
 `;

const SectionBody = styled.div`
   padding: 1.5rem;
 
   @media (max-width: 480px) {
     padding: 1rem;
   }
 `;

const SectionFooter = styled.div`
   padding: 1rem 1.5rem;
   background: #fafbfc;
   border-top: 1px solid #e1e5e9;
   display: flex;
   justify-content: flex-end;
   gap: 0.75rem;
 
   @media (max-width: 480px) {
     padding: 1rem;
     flex-direction: column;
   }
 `;

const Button = styled.button`
   background: ${props => {
    switch (props.variant) {
      case 'danger': return '#E53935';
      case 'outline': return 'transparent';
      case 'secondary': return '#1A2E40';
      default: return '#4CAF50';
    }
  }};
   color: ${props => props.variant === 'outline' ? '#4CAF50' : 'white'};
   border: 2px solid ${props => props.variant === 'outline' ? '#4CAF50' : 'transparent'};
   padding: 0.7rem 1.5rem;
   border-radius: 8px;
   font-size: 0.95rem;
   font-weight: 600;
   font-family: 'Roboto', sans-serif;
   cursor: pointer;
   box-shadow: ${props => props.variant === 'outline' ? 'none' : '0 2px 8px rgba(76, 175, 80, 0.15)'};
   min-height: 44px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 0.5rem;
 
   &:focus-visible {
     outline: 2px solid #4CAF50;
     outline-offset: 2px;
   }
 
   @media (max-width: 480px) {
     min-height: 48px;
     width: 100%;
   }
 `;

const ButtonSmall = styled.button`
   background: ${props => {
    if (props.className?.includes('danger')) return '#E53935';
    if (props.className?.includes('secondary')) return '#1A2E40';
    return '#4CAF50';
  }};
   color: white;
   border: none;
   border-radius: 6px;
   padding: 0.7rem 1.5rem;
   font-family: 'Roboto', sans-serif;
   font-weight: 600;
   cursor: pointer;
   font-size: 0.95rem;
   box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
   min-height: 44px;
 
   @media (max-width: 480px) {
     min-height: 48px;
     padding: 0.7rem 1rem;
     font-size: 0.875rem;
   }
 `;

const FormInput = styled.input`
   width: 100%;
   padding: 0.75rem;
   border: 2px solid #e1e5e9;
   border-radius: 8px;
   font-family: 'Roboto', sans-serif;
   font-size: 16px;
   background: white;
   color: #333333;
   min-height: 44px;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
   box-sizing: border-box;
 
   &:focus {
     outline: none;
     border-color: #4CAF50;
     box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
   }
 
   &:focus-visible {
     outline: 2px solid #4CAF50;
     outline-offset: 2px;
   }
 
   &.error {
     border-color: #E53935 !important;
     background-color: #fef2f2 !important;
     box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
   }
 
   &.valid {
     border-color: #4CAF50 !important;
     background-color: #f0f9ff !important;
     box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1) !important;
   }
 
   @media (max-width: 480px) {
     min-height: 48px;
   }
 `;

const FormSelect = styled.select`
   width: 100%;
   padding: 0.75rem;
   border: 2px solid #e1e5e9;
   border-radius: 8px;
   font-family: 'Roboto', sans-serif;
   font-size: 16px;
   background: white;
   color: #333333;
   min-height: 44px;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
   box-sizing: border-box;
 
   &:focus {
     outline: none;
     border-color: #4CAF50;
     box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
   }
 
   &:focus-visible {
     outline: 2px solid #4CAF50;
     outline-offset: 2px;
   }
 
   &.error {
     border-color: #E53935 !important;
     background-color: #fef2f2 !important;
     box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
   }
 
   &.valid {
     border-color: #4CAF50 !important;
     background-color: #f0f9ff !important;
     box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1) !important;
   }
 
   @media (max-width: 480px) {
     min-height: 48px;
   }
 `;

const FormLabel = styled.label`
   display: block;
   font-weight: 500;
   color: #333333;
   margin-bottom: 0.5rem;
   font-family: 'Roboto', sans-serif;
   font-size: 0.9rem;
 `;

const FormGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
   gap: 1.5rem;
   align-items: end;
   ${props => props.marginBottom && `margin-bottom: ${props.marginBottom};`}
   ${props => props.marginTop && `margin-top: ${props.marginTop};`}
 
   @media (max-width: 480px) {
     grid-template-columns: 1fr;
     gap: 1rem;
   }
 
   @media (min-width: 481px) and (max-width: 1024px) {
     grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
   }
 
   @media (min-width: 1025px) {
     grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
   }
 `;

const FormGroup = styled.div`
   display: flex;
   flex-direction: column;
 `;

const InfoBox = styled.div`
   background: linear-gradient(135deg, #F0F8FF, #E8F5E8);
   border: 1px solid #4CAF50;
   border-radius: 12px;
   padding: 1rem;
   margin-bottom: 1.5rem;
   color: #1A2E40;
   font-family: 'Roboto', sans-serif;
   ${props => props.marginTop && `margin-top: ${props.marginTop};`}
 
   h3 {
     font-family: 'Poppins', 'Roboto', sans-serif;
     color: #1A2E40;
     margin-bottom: 1rem;
     font-size: 1.1rem;
     font-weight: 700;
   }
 `;

const MacroGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
   gap: 1rem;
 
   @media (max-width: 480px) {
     grid-template-columns: 1fr;
   }
 
   @media (min-width: 481px) and (max-width: 1024px) {
     grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
   }
 
   @media (min-width: 1024px) {
     grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
   }
 `;

const MacroItem = styled.label`
   display: flex;
   align-items: center;
   gap: 0.75rem;
   cursor: pointer;
   padding: 0.75rem;
   background: linear-gradient(135deg, #ffffff, #fafbfc);
   border-radius: 12px;
   border: 2px solid #e1e5e9;
 `;

const MacroCheckbox = styled.input`
   width: 20px;
   height: 20px;
   accent-color: #4CAF50;
   cursor: pointer;
   flex-shrink: 0;
 `;

const MacroInfo = styled.div`
   flex: 1;
   min-width: 0;
 `;

const MacroLabel = styled.div`
   font-weight: 600;
   color: #1A2E40;
   font-size: 0.9rem;
   font-family: 'Poppins', 'Roboto', sans-serif;
 `;

const MacroUnit = styled.div`
   font-size: 0.8rem;
   color: #64748b;
   font-weight: 500;
 `;

const StatsGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
   gap: 1rem;
 
   @media (max-width: 480px) {
     grid-template-columns: 1fr;
   }
 
   @media (min-width: 481px) and (max-width: 1024px) {
     grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
   }
 `;

const StatItem = styled.div`
   text-align: center;
   padding: 1rem;
   background: linear-gradient(135deg, #ffffff, #fafbfc);
   border-radius: 12px;
   border: 1px solid #e1e5e9;
   box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
 `;

const StatValue = styled.div`
   font-family: 'Roboto Mono', monospace;
   font-size: 1.5rem;
   font-weight: bold;
   margin-bottom: 0.25rem;
   display: block;
   color: ${props => {
    switch (props.variant) {
      case 'primary': return '#4CAF50';
      case 'secondary': return '#1A2E40';
      case 'accent': return '#E53935';
      default: return '#333333';
    }
  }};
 `;

const StatLabel = styled.div`
   font-size: 0.9rem;
   color: #64748b;
   font-weight: 500;
 `;

const ActionButtons = styled.div`
   display: flex;
   gap: 0.5rem;
   flex-wrap: wrap;
   align-items: center;
   margin-bottom: 1.5rem;
 
   @media (max-width: 480px) {
     justify-content: stretch;
     flex-direction: column;
   }
 `;

const MacroCount = styled.span`
   background: #4CAF50;
   color: white;
   border-radius: 12px;
   padding: 0.25rem 0.5rem;
   font-size: 0.8rem;
   font-weight: 500;
   margin-left: 0.5rem;
 `;

const ValidationError = styled.div`
   color: #E53935;
   font-size: 0.875rem;
   margin-top: 0.25rem;
   display: flex;
   align-items: center;
   gap: 0.25rem;
   font-family: 'Roboto', sans-serif;
   font-weight: 500;
 
   &::before {
     content: '!';
     font-size: 0.75rem;
   }
 `;

const NutritionSetting = () => {
  const getDefaultSettings = () => ({
    profile: {
      name: 'Utilisateur',
      age: 30,
      gender: 'male',
      weight: 70,
      height: 175,
      activity: 'moderate'
    },
    goals: {
      targetCalories: 2000,
      targetProtein: 120,
      targetCarbs: 250,
      targetFats: 70,
      advancedMacroGoalsEnabled: false,
      targetFiber: '',
      targetSugars: '',
      targetSodium: '',
      targetSaturatedFat: '',
      goalType: 'maintain'
    },
    preferences: {
      units: 'metric',
      language: 'fr',
      notifications: true,
      autoCalculate: true,
      showMacroPercentages: true,
      theme: 'light'
    }
  });

  const [journalId, setJournalId] = useState(null);
  const [settings, setSettings] = useState(getDefaultSettings());
  const [alertState, setAlertState] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);
  const [errors, setErrors] = useState({});
  const [bmr, setBmr] = useState(1500);
  const [tdee, setTdee] = useState(2000);
  const [suggestedCalories, setSuggestedCalories] = useState(2000);
  const [summaryMacros, setSummaryMacros] = useState([]);
  const [summaryMacroCount, setSummaryMacroCount] = useState(SUMMARY_STANDARD_MACRO_KEYS.length);
  const [hideUnavailableFoodMacros, setHideUnavailableFoodMacros] = useState(false);
  const [publicJournal, setPublicJournal] = useState(true);
  const [, setSavedState] = useState({
    goals: null,
    summaryMacros: [...SUMMARY_STANDARD_MACRO_KEYS],
    hideUnavailableFoodMacros: false,
    publicJournal: true,
  });
  const { user } = useAuth();

  const isPremium = hasPremiumAccess(user);

  useEffect(() => {
    loadSettings();
  }, [isPremium]);

  useEffect(() => {
    updateCalculatedGoals();
  }, [settings.profile, settings.goals.goalType]);

  useEffect(() => {
    setSummaryMacroCount(summaryMacros.length);
  }, [summaryMacros]);

  const normalizeSummaryMacros = useCallback((macros, advancedEnabled) => {
    const values = Array.isArray(macros) ? macros : [];
    return Array.from(
      new Set(
        values
          .filter((macroKey) => SUMMARY_ALLOWED_MACRO_KEYS.includes(macroKey))
          .filter((macroKey) => advancedEnabled || !ADVANCED_SUMMARY_MACRO_KEYS.includes(macroKey))
      )
    );
  }, []);

  const buildGoalsSectionSnapshot = useCallback((allSettings) => {
    const profile = allSettings?.profile || {};
    const goals = allSettings?.goals || {};
    return {
      age: Number(profile.age) || 0,
      gender: String(profile.gender || ''),
      weight: Number(profile.weight) || 0,
      height: Number(profile.height) || 0,
      activity: String(profile.activity || ''),
      goalType: String(goals.goalType || ''),
      targetCalories: Number(goals.targetCalories) || 0,
      targetProtein: Number(goals.targetProtein) || 0,
      targetCarbs: Number(goals.targetCarbs) || 0,
      targetFats: Number(goals.targetFats) || 0,
      advancedMacroGoalsEnabled: isPremium && Boolean(goals.advancedMacroGoalsEnabled),
      targetFiber: goals.targetFiber === '' ? '' : (Number(goals.targetFiber) || ''),
      targetSugars: goals.targetSugars === '' ? '' : (Number(goals.targetSugars) || ''),
      targetSodium: goals.targetSodium === '' ? '' : (Number(goals.targetSodium) || ''),
      targetSaturatedFat: goals.targetSaturatedFat === '' ? '' : (Number(goals.targetSaturatedFat) || ''),
    };
  }, [isPremium]);

  const loadSettings = async () => {
    try {
      const journalData = await settingsJournalService.loadUserJournal();
      setJournalId(journalData.id);

      const goals = await settingsJournalService.getNutritionalGoals(journalData.id);

      const localSettings = getAppStorage().getItem(SETTINGS_STORAGE_KEY);
      let parsedLocalSettings = {};
      if (localSettings) {
        parsedLocalSettings = JSON.parse(localSettings);
      }

      const loadedSettings = {
        ...getDefaultSettings(),
        ...parsedLocalSettings,
        goals: {
          targetCalories: goals.targetCalories || 2000,
          targetProtein: goals.targetProtein || 120,
          targetCarbs: goals.targetCarbs || 250,
          targetFats: goals.targetFats || 70,
          advancedMacroGoalsEnabled: Boolean(goals.advancedMacroGoalsEnabled),
          targetFiber: Number.isFinite(goals.targetFiber) ? goals.targetFiber : '',
          targetSugars: Number.isFinite(goals.targetSugars) ? goals.targetSugars : '',
          targetSodium: Number.isFinite(goals.targetSodium) ? goals.targetSodium : '',
          targetSaturatedFat: Number.isFinite(goals.targetSaturatedFat) ? goals.targetSaturatedFat : '',
          goalType: parsedLocalSettings.goals?.goalType || 'maintain'
        }
      };
      setSettings(loadedSettings);

      const advancedEnabled = Boolean(goals.advancedMacroGoalsEnabled);
      const savedSummaryMacros = Array.isArray(goals.summaryMacros) ? goals.summaryMacros : [];
      const sanitizedSummaryMacros = normalizeSummaryMacros(savedSummaryMacros, advancedEnabled);
      const initialSummaryMacros = sanitizedSummaryMacros.length > 0
        ? sanitizedSummaryMacros
        : [...SUMMARY_STANDARD_MACRO_KEYS];

      setSummaryMacros(initialSummaryMacros);

      setHideUnavailableFoodMacros(Boolean(goals.hideUnavailableFoodMacros));

      // Charger le paramètre Journal public
      const resolvedPublicJournal = goals.publicJournal === undefined
        ? true
        : (goals.publicJournal === true || goals.publicJournal === 'true');
      if (goals.publicJournal !== undefined) {
        setPublicJournal(resolvedPublicJournal);
      }

      setSavedState({
        goals: buildGoalsSectionSnapshot(loadedSettings),
        summaryMacros: initialSummaryMacros,
        hideUnavailableFoodMacros: Boolean(goals.hideUnavailableFoodMacros),
        publicJournal: resolvedPublicJournal,
      });

      window.dispatchEvent(new CustomEvent('nutritionGoals:changed'));
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
      setSettings(getDefaultSettings());
    }
  };

  const closeModal = useCallback(() => {
    setShowModal(false);
    setModalConfig(null);
  }, []);

  const showToast = useCallback((message, type = 'info') => {
    setAlertState({ message, type });
    setTimeout(() => setAlertState(null), 3200);
  }, []);

  const validateInput = (name, value) => {
    const validationRules = {
      age: { required: true, range: [15, 100] },
      weight: { required: true, range: [30, 300] },
      height: { required: true, range: [120, 250] },
      targetCalories: { required: true, range: [1000, 5000] },
      targetProtein: { required: true, range: [30, 300] },
      targetCarbs: { required: true, range: [50, 500] },
      targetFats: { required: true, range: [20, 200] },
      targetFiber: { required: false, range: [1, 120] },
      targetSugars: { required: false, range: [1, 300] },
      targetSodium: { required: false, range: [1, 20000] },
      targetSaturatedFat: { required: false, range: [1, 150] }
    };

    const rules = validationRules[name];
    if (!rules) return true;

    const newErrors = [];

    if (!rules.required && (value === '' || value === null || value === undefined)) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
      return true;
    }

    if (rules.required && (value === '' || value === null || value === undefined)) {
      newErrors.push('Ce champ est requis');
    } else if (rules.range) {
      const numValue = parseFloat(value);
      if (numValue < rules.range[0] || numValue > rules.range[1]) {
        newErrors.push(`La valeur doit être entre ${rules.range[0]} et ${rules.range[1]}`);
      }
    }

    setErrors(prev => ({
      ...prev,
      [name]: newErrors.length > 0 ? newErrors[0] : null
    }));

    return newErrors.length === 0;
  };

  // Fixed: was `settings.profile[input] || settings.goals[input]`, which
  // silently validated the wrong (undefined) value whenever the real
  // value was falsy (e.g. age/weight/height === 0).
  const validateAllInputs = () => {
    const fieldsToValidate = {
      age: settings.profile.age,
      weight: settings.profile.weight,
      height: settings.profile.height,
      targetCalories: settings.goals.targetCalories,
      targetProtein: settings.goals.targetProtein,
      targetCarbs: settings.goals.targetCarbs,
      targetFats: settings.goals.targetFats,
    };

    let allValid = true;
    Object.entries(fieldsToValidate).forEach(([name, value]) => {
      if (!validateInput(name, value)) {
        allValid = false;
      }
    });

    return allValid;
  };

  // Single source of truth for BMR/TDEE/suggested calories (see
  // utils/nutritionCalculations.js). NaN-safe: clearing a profile field
  // while typing no longer blanks out the preview with "NaN".
  const updateCalculatedGoals = () => {
    const stats = calculateMetabolicStats(settings.profile, settings.goals.goalType);
    setBmr(stats.bmr);
    setTdee(stats.tdee);
    setSuggestedCalories(stats.suggestedCalories);
  };

  // Was duplicating updateCalculatedGoals' BMR/TDEE formula inline, plus
  // a flat 2.2 g/kg protein target regardless of goal. Now shares the
  // exact same stats via calculateMacroTargets/calculateAdvancedMacroTargets,
  // with protein scaled per goal (higher in a deficit to protect lean mass).
  const autoCalculateGoals = () => {
    const macros = calculateMacroTargets(settings.profile, settings.goals.goalType);
    const shouldApplyAdvanced = isPremium && Boolean(settings.goals.advancedMacroGoalsEnabled);
    const advanced = shouldApplyAdvanced
      ? calculateAdvancedMacroTargets(settings.profile, settings.goals.goalType)
      : {};

    setSettings(prev => ({
      ...prev,
      goals: {
        ...prev.goals,
        ...macros,
        ...advanced
      }
    }));

    showToast(
      shouldApplyAdvanced
        ? 'Objectifs calculés appliqués (standards + avancés) ! N\'oublie pas de cliquer sur "Sauvegarder" dans la section du dessous pour les enregistrer.'
        : 'Objectifs calculés appliqués aux champs ! N\'oublie pas de cliquer sur "Sauvegarder" dans la section du dessous pour les enregistrer.',
      'success'
    );
  };

  const saveGoalsSettings = () => {
    if (!validateAllInputs()) {
      setModalConfig({
        title: 'Erreur de validation',
        message: 'Veuillez corriger les erreurs avant de sauvegarder.',
        onConfirm: closeModal,
        singleButton: true
      });
      setShowModal(true);
      return;
    }

    setModalConfig({
      title: 'Confirmation',
      message: 'Voulez-vous enregistrer les objectifs nutritionnels ?',
      onConfirm: async () => {
        try {
          getAppStorage().setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));

          if (journalId) {
            await settingsJournalService.updateNutritionalGoals(journalId, {
              targetCalories: settings.goals.targetCalories,
              targetProtein: settings.goals.targetProtein,
              targetCarbs: settings.goals.targetCarbs,
              targetFats: settings.goals.targetFats,
              advancedMacroGoalsEnabled: isPremium && Boolean(settings.goals.advancedMacroGoalsEnabled),
              targetFiber: settings.goals.targetFiber,
              targetSugars: settings.goals.targetSugars,
              targetSodium: settings.goals.targetSodium,
              targetSaturatedFat: settings.goals.targetSaturatedFat
            });
          }

          setSavedState((prev) => ({
            ...prev,
            goals: buildGoalsSectionSnapshot(settings),
          }));

          showToast('Objectifs sauvegardés avec succès !', 'success');
          window.dispatchEvent(new CustomEvent('nutritionGoals:changed'));
          closeModal();
        } catch (error) {
          showToast('Erreur lors de la sauvegarde', 'error');
          closeModal();
        }
      },
      onCancel: closeModal
    });
    setShowModal(true);
  };

  const saveSummaryMacros = () => {
    setModalConfig({
      title: 'Confirmation',
      message: 'Voulez-vous enregistrer les paramètres du résumé journalier ?',
      onConfirm: async () => {
        try {
          const advancedEnabled = isPremium && Boolean(settings.goals.advancedMacroGoalsEnabled);
          const sanitizedSummaryMacros = normalizeSummaryMacros(summaryMacros, advancedEnabled);

          if (journalId) {
            await settingsJournalService.updateSummaryMacros(journalId, sanitizedSummaryMacros);
          }
          setSummaryMacros(sanitizedSummaryMacros);
          setSavedState((prev) => ({
            ...prev,
            summaryMacros: sanitizedSummaryMacros,
          }));
          showToast('Paramètres du résumé sauvegardés !', 'success');
          // Fixed: manquait ce dispatch -> le Journal (s'il était déjà
          // monté/ouvert) ne rechargeait jamais la nouvelle sélection de
          // macros tant qu'on ne rafraîchissait pas la page à la main.
          window.dispatchEvent(new CustomEvent('nutritionGoals:changed'));
          closeModal();
        } catch (error) {
          console.error('Erreur sauvegarde macros résumé:', error);
          showToast('Erreur lors de la sauvegarde', 'error');
          closeModal();
        }
      },
      onCancel: closeModal
    });
    setShowModal(true);
  };

  const saveFoodDisplaySettings = () => {
    setModalConfig({
      title: 'Confirmation',
      message: 'Voulez-vous enregistrer ce paramètre d’affichage des aliments ?',
      onConfirm: async () => {
        try {
          if (journalId) {
            await settingsJournalService.updateHideUnavailableFoodMacros(journalId, hideUnavailableFoodMacros);
          }
          setSavedState((prev) => ({
            ...prev,
            hideUnavailableFoodMacros: Boolean(hideUnavailableFoodMacros),
          }));
          showToast('Paramètre d’affichage sauvegardé !', 'success');
          // Fixed: même souci que saveSummaryMacros, ce paramètre est lu
          // par le Journal (hideUnavailableFoodMacros) et ne se
          // répercutait pas sans rechargement manuel de la page.
          window.dispatchEvent(new CustomEvent('nutritionGoals:changed'));
          closeModal();
        } catch (error) {
          console.error('Erreur sauvegarde affichage aliments:', error);
          showToast('Erreur lors de la sauvegarde', 'error');
          closeModal();
        }
      },
      onCancel: closeModal
    });
    setShowModal(true);
  };

  const savePublicJournal = () => {
    setModalConfig({
      title: 'Confirmation',
      message: 'Voulez-vous enregistrer le paramètre de visibilité du journal ?',
      onConfirm: async () => {
        try {
          if (!journalId) {
            throw new Error('Journal introuvable. Rechargez la page avant de sauvegarder.');
          }

          await settingsJournalService.updateJournalSetting(journalId, 'public_journal', publicJournal);

          const refreshedGoals = await settingsJournalService.getNutritionalGoals(journalId);
          if (refreshedGoals.publicJournal !== undefined) {
            setPublicJournal(refreshedGoals.publicJournal === true || refreshedGoals.publicJournal === 'true');
          }
          setSavedState((prev) => ({
            ...prev,
            publicJournal: Boolean(publicJournal),
          }));

          showToast('Paramètre de visibilité sauvegardé !', 'success');
          closeModal();
        } catch (error) {
          console.error('Erreur sauvegarde visibilité journal:', error);
          showToast(error?.message || 'Erreur lors de la sauvegarde', 'error');
          closeModal();
        }
      },
      onCancel: closeModal
    });
    setShowModal(true);
  };

  const selectEssentialSummaryMacros = () => {
    setSummaryMacros([...SUMMARY_STANDARD_MACRO_KEYS]);
  };

  const selectAllSummaryMacros = () => {
    const advancedEnabled = isPremium && Boolean(settings.goals.advancedMacroGoalsEnabled);
    setSummaryMacros(
      advancedEnabled
        ? [...SUMMARY_ALLOWED_MACRO_KEYS]
        : [...SUMMARY_STANDARD_MACRO_KEYS]
    );
  };

  const clearSummaryMacros = () => {
    setSummaryMacros([]);
  };

  const Toast = () => {
    if (!alertState?.message) return null;

    return (
      <Alert
        type={alertState.type || 'info'}
        message={alertState.message}
        duration={3200}
        onClose={() => setAlertState(null)}
      />
    );
  };

  return (
    <>
      {showModal && modalConfig && (
        <Modal
          title={modalConfig.title}
          message={modalConfig.message}
          onConfirm={modalConfig.onConfirm}
          onCancel={modalConfig.onCancel}
          confirmText={modalConfig.confirmText}
          cancelText={modalConfig.cancelText}
          singleButton={modalConfig.singleButton}
          className={modalConfig.className}
        >
          {modalConfig.children}
        </Modal>
      )}

      <Toast />

      <SettingsContainer>
        <SettingsSection>
          <SectionHeader>
            <SectionTitle>
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
              Objectifs nutritionnels (saisie automatique)
            </SectionTitle>
          </SectionHeader>

          <SectionBody>
            <FormGrid>
              <FormGroup>
                <FormLabel htmlFor="age">Âge (années)</FormLabel>
                <FormInput
                  type="number"
                  id="age"
                  min="15"
                  max="100"
                  value={settings.profile.age}
                  onChange={(e) => {
                    const value = e.target.value === '' ? '' : parseInt(e.target.value);
                    validateInput('age', value);
                    setSettings(prev => ({
                      ...prev,
                      profile: { ...prev.profile, age: value }
                    }));
                  }}
                  className={errors.age ? 'error' : (settings.profile.age ? 'valid' : '')}
                />
                {errors.age && <ValidationError>{errors.age}</ValidationError>}
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="gender">Sexe</FormLabel>
                <FormSelect
                  id="gender"
                  value={settings.profile.gender}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    profile: { ...prev.profile, gender: e.target.value }
                  }))}
                >
                  <option value="male">Homme</option>
                  <option value="female">Femme</option>
                </FormSelect>
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="weight">Poids (kg)</FormLabel>
                <FormInput
                  type="number"
                  id="weight"
                  min="30"
                  max="300"
                  step="0.1"
                  value={settings.profile.weight}
                  onChange={(e) => {
                    const value = e.target.value === '' ? '' : parseFloat(e.target.value);
                    validateInput('weight', value);
                    setSettings(prev => ({
                      ...prev,
                      profile: { ...prev.profile, weight: value }
                    }));
                  }}
                  className={errors.weight ? 'error' : (settings.profile.weight ? 'valid' : '')}
                />
                {errors.weight && <ValidationError>{errors.weight}</ValidationError>}
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="height">Taille (cm)</FormLabel>
                <FormInput
                  type="number"
                  id="height"
                  min="120"
                  max="250"
                  value={settings.profile.height}
                  onChange={(e) => {
                    const value = e.target.value === '' ? '' : parseInt(e.target.value);
                    validateInput('height', value);
                    setSettings(prev => ({
                      ...prev,
                      profile: { ...prev.profile, height: value }
                    }));
                  }}
                  className={errors.height ? 'error' : (settings.profile.height ? 'valid' : '')}
                />
                {errors.height && <ValidationError>{errors.height}</ValidationError>}
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="activity">Niveau d'activité</FormLabel>
                <FormSelect
                  id="activity"
                  value={settings.profile.activity}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    profile: { ...prev.profile, activity: e.target.value }
                  }))}
                >
                  <option value="sedentary">Sédentaire (bureau, peu d'exercice)</option>
                  <option value="light">Légèrement actif (exercice léger 1-3j/sem)</option>
                  <option value="moderate">Modérément actif (exercice modéré 3-5j/sem)</option>
                  <option value="active">Très actif (exercice intense 6-7j/sem)</option>
                  <option value="veryActive">Extrêmement actif (exercice intense quotidien)</option>
                </FormSelect>
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="goalType">Type d'objectif</FormLabel>
                <FormSelect
                  id="goalType"
                  value={settings.goals.goalType}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    goals: { ...prev.goals, goalType: e.target.value }
                  }))}
                >
                  <option value="lose">Perdre du poids (-500 kcal/jour)</option>
                  <option value="maintain">Maintenir le poids</option>
                  <option value="gain">Prendre du poids (+500 kcal/jour)</option>
                </FormSelect>
              </FormGroup>
            </FormGrid>

            <InfoBox marginTop="1.5rem">
              <h3>Calculs métaboliques</h3>
              <StatsGrid>
                <StatItem>
                  <StatValue variant="primary">{bmr}</StatValue>
                  <StatLabel>BMR (kcal/jour)</StatLabel>
                </StatItem>
                <StatItem>
                  <StatValue variant="secondary">{tdee}</StatValue>
                  <StatLabel>TDEE (kcal/jour)</StatLabel>
                </StatItem>
                <StatItem>
                  <StatValue variant="accent">{suggestedCalories}</StatValue>
                  <StatLabel>Calories suggérées</StatLabel>
                </StatItem>
              </StatsGrid>
            </InfoBox>
          </SectionBody>

          <SectionFooter>
            {/* Fixed: ce bouton ne sauvegardait rien côté serveur, il ne
                fait que pré-remplir les champs de la section du dessous.
                L'étiquette "Sauvegarder" était trompeuse. */}
            <Button onClick={autoCalculateGoals}>
              Calculer automatiquement
            </Button>
          </SectionFooter>
        </SettingsSection>

        <SettingsSection>
          <SectionHeader>
            <SectionTitle>
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
              Objectifs nutritionnels (saisie manuelle)
            </SectionTitle>
          </SectionHeader>

          <SectionBody>

            <FormGrid marginTop="1.5rem">
              <FormGroup>
                <FormLabel htmlFor="targetCalories">Calories (kcal/jour)</FormLabel>
                <FormInput
                  type="number"
                  id="targetCalories"
                  min="1000"
                  max="5000"
                  value={settings.goals.targetCalories}
                  onChange={(e) => {
                    const value = e.target.value === '' ? '' : parseInt(e.target.value);
                    validateInput('targetCalories', value);

                    setSettings(prev => {
                      if (value === '') {
                        return { ...prev, goals: { ...prev.goals, targetCalories: value } };
                      }
                      const scaled = scaleMacrosToCalories(
                        value,
                        prev.goals.targetProtein,
                        prev.goals.targetCarbs,
                        prev.goals.targetFats
                      );

                      return {
                        ...prev,
                        goals: {
                          ...prev.goals,
                          targetCalories: value,
                          targetProtein: scaled.protein,
                          targetCarbs: scaled.carbs,
                          targetFats: scaled.fats,
                        }
                      };
                    });
                  }}
                  className={errors.targetCalories ? 'error' : (settings.goals.targetCalories ? 'valid' : '')}
                />
                {errors.targetCalories && <ValidationError>{errors.targetCalories}</ValidationError>}
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="targetProtein">Protéines (g/jour)</FormLabel>
                <FormInput
                  type="number"
                  id="targetProtein"
                  min="30"
                  max="300"
                  value={settings.goals.targetProtein}
                  onChange={(e) => {
                    const value = e.target.value === '' ? '' : parseInt(e.target.value);
                    validateInput('targetProtein', value);
                    setSettings(prev => ({
                      ...prev,
                      goals: {
                        ...prev.goals,
                        targetProtein: value,
                        targetCalories: caloriesFromMacros(value, prev.goals.targetCarbs, prev.goals.targetFats)
                      }
                    }));
                  }}
                  className={errors.targetProtein ? 'error' : (settings.goals.targetProtein ? 'valid' : '')}
                />
                {errors.targetProtein && <ValidationError>{errors.targetProtein}</ValidationError>}
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="targetCarbs">Glucides (g/jour)</FormLabel>
                <FormInput
                  type="number"
                  id="targetCarbs"
                  min="50"
                  max="500"
                  value={settings.goals.targetCarbs}
                  onChange={(e) => {
                    const value = e.target.value === '' ? '' : parseInt(e.target.value);
                    validateInput('targetCarbs', value);

                    // Fixed: Fibres et Sucres sont des sous-composantes des
                    // Glucides. Si on baisse les Glucides en dessous d'une
                    // valeur de Fibres/Sucres déjà saisie, on les ramène au
                    // nouveau plafond pour garder des valeurs cohérentes
                    // (sinon on pouvait se retrouver avec "Sucres: 40g"
                    // pour "Glucides: 30g").
                    const currentFiber = settings.goals.targetFiber;
                    const currentSugars = settings.goals.targetSugars;
                    const fiberExceedsNewCarbs = value !== '' && currentFiber !== '' && Number(currentFiber) > value;
                    const sugarsExceedNewCarbs = value !== '' && currentSugars !== '' && Number(currentSugars) > value;

                    setSettings(prev => ({
                      ...prev,
                      goals: {
                        ...prev.goals,
                        targetCarbs: value,
                        targetFiber: fiberExceedsNewCarbs ? value : prev.goals.targetFiber,
                        targetSugars: sugarsExceedNewCarbs ? value : prev.goals.targetSugars,
                        targetCalories: caloriesFromMacros(prev.goals.targetProtein, value, prev.goals.targetFats)
                      }
                    }));

                    if (fiberExceedsNewCarbs) {
                      setErrors(prev => ({ ...prev, targetFiber: null }));
                    }
                    if (sugarsExceedNewCarbs) {
                      setErrors(prev => ({ ...prev, targetSugars: null }));
                    }
                  }}
                  className={errors.targetCarbs ? 'error' : (settings.goals.targetCarbs ? 'valid' : '')}
                />
                {errors.targetCarbs && <ValidationError>{errors.targetCarbs}</ValidationError>}
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="targetFats">Lipides (g/jour)</FormLabel>
                <FormInput
                  type="number"
                  id="targetFats"
                  min="20"
                  max="200"
                  value={settings.goals.targetFats}
                  onChange={(e) => {
                    const value = e.target.value === '' ? '' : parseInt(e.target.value);
                    validateInput('targetFats', value);

                    // Fixed: même logique que pour Glucides/Fibres-Sucres,
                    // mais pour Lipides/AG saturés.
                    const currentSaturatedFat = settings.goals.targetSaturatedFat;
                    const saturatedFatExceedsNewFats = value !== '' && currentSaturatedFat !== '' && Number(currentSaturatedFat) > value;

                    setSettings(prev => ({
                      ...prev,
                      goals: {
                        ...prev.goals,
                        targetFats: value,
                        targetSaturatedFat: saturatedFatExceedsNewFats ? value : prev.goals.targetSaturatedFat,
                        targetCalories: caloriesFromMacros(prev.goals.targetProtein, prev.goals.targetCarbs, value)
                      }
                    }));

                    if (saturatedFatExceedsNewFats) {
                      setErrors(prev => ({ ...prev, targetSaturatedFat: null }));
                    }
                  }}
                  className={errors.targetFats ? 'error' : (settings.goals.targetFats ? 'valid' : '')}
                />
                {errors.targetFats && <ValidationError>{errors.targetFats}</ValidationError>}
              </FormGroup>
            </FormGrid>
          </SectionBody>

          <SectionFooter>
            <Button onClick={saveGoalsSettings}>
              Sauvegarder
            </Button>
          </SectionFooter>
        </SettingsSection>

        <SettingsSection>
          <SectionHeader>
            <SectionTitle>
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path d="M4 7a3 3 0 1 1 6 0 3 3 0 0 1-6 0Zm10 10a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM7 8h13v2H7V8Zm-3 8h13v2H4v-2Z" />
              </svg>
              Affichage des aliments
            </SectionTitle>
          </SectionHeader>

          <SectionBody>
            <InfoBox>
              <strong>Info :</strong> Ce paramètre agit sur les cartes aliments et leurs détails. Les macros sans valeur fournie ne seront plus affichées si vous activez l’option.
            </InfoBox>

            <SwitchButton
              title="Masquer les macros indisponibles pour un aliment"
              description="Les macros absentes ou non renseignées sont masquées dans les aperçus et les détails des aliments."
              checked={hideUnavailableFoodMacros}
              onChange={setHideUnavailableFoodMacros}
            />
          </SectionBody>

          <SectionFooter>
            <Button onClick={saveFoodDisplaySettings}>
              Sauvegarder l’affichage des aliments
            </Button>
          </SectionFooter>
        </SettingsSection>

        <SettingsSection>
          <SectionHeader>
            <SectionTitle>
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" />
              </svg>
              Résumé journalier
              <MacroCount>{summaryMacroCount}</MacroCount>
            </SectionTitle>
          </SectionHeader>

          <SectionBody>
            <InfoBox>
              <strong>Info :</strong> Configurez les macros qui apparaîtront dans le résumé journalier affiché en bas du journal. Les macros avec un objectif défini afficheront également les barres de progression.
            </InfoBox>

            <ActionButtons>
              <ButtonSmall onClick={selectEssentialSummaryMacros}>
                Essentielles
              </ButtonSmall>

              <ButtonSmall className="secondary" onClick={selectAllSummaryMacros}>
                Tout sélectionner
              </ButtonSmall>

              <ButtonSmall className="danger" onClick={clearSummaryMacros}>
                Tout désélectionner
              </ButtonSmall>
            </ActionButtons>

            <MacroGrid>
              {SUMMARY_ALLOWED_MACRO_KEYS.map(key => {
                const macro = AVAILABLE_MACROS.find(m => m.key === key);
                if (!macro) return null;
                const isAdvancedMacro = ADVANCED_SUMMARY_MACRO_KEYS.includes(macro.key);
                const advancedEnabled = isPremium && Boolean(settings.goals.advancedMacroGoalsEnabled);
                const isLocked =
                  (!isPremium && PREMIUM_ONLY_MACROS.includes(macro.key))
                  || (isAdvancedMacro && !advancedEnabled);
                return (
                  <MacroItem key={macro.key}>
                    <MacroCheckbox
                      type="checkbox"
                      checked={summaryMacros.includes(macro.key)}
                      disabled={isLocked}
                      onChange={() => {
                        if (isLocked) return;
                        const newSummaryMacros = summaryMacros.includes(macro.key)
                          ? summaryMacros.filter(m => m !== macro.key)
                          : [...summaryMacros, macro.key];
                        setSummaryMacros(newSummaryMacros);
                      }}
                    />
                    <MacroInfo>
                      <MacroLabel>
                        {macro.label}
                        {isLocked && (
                          <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#E53935', fontWeight: 700 }}>
                            {isAdvancedMacro ? 'MODE AVANCÉ' : 'PREMIUM'}
                          </span>
                        )}
                      </MacroLabel>
                      <MacroUnit>{macro.unit}</MacroUnit>
                    </MacroInfo>
                  </MacroItem>
                );
              })}
            </MacroGrid>
          </SectionBody>

          <SectionFooter>
            <Button onClick={saveSummaryMacros}>
              Sauvegarder le résumé journalier
            </Button>
          </SectionFooter>
        </SettingsSection>

        <SettingsSection>
          <SectionHeader>
            <SectionTitle>
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
              </svg>
              Visibilité du journal
            </SectionTitle>
          </SectionHeader>

          <SectionBody>
            <InfoBox>
              <strong>Info :</strong> {`Ce paramètre contrôle si votre journal nutritionnel est visible par les autres utilisateurs de ${SITE_NAME}.`}
            </InfoBox>

            <SwitchButton
              title="Journal public"
              description="Permettre aux autres utilisateurs de trouver votre profil dans les recherches"
              checked={publicJournal}
              onChange={setPublicJournal}
            />
          </SectionBody>

          <SectionFooter>
            <Button onClick={savePublicJournal}>
              Sauvegarder la visibilité
            </Button>
          </SectionFooter>
        </SettingsSection>
      </SettingsContainer>
    </>
  );
};

export default NutritionSetting;